import axios from "axios";
import { WEBOT_SERVER_ENDPOINT } from "../logic/Consts";
import { signerSessionHeaders } from "./signerSession";
import type {
  ListingListedFor,
  SignatureDocumentKind,
  SellerQuestionnaireAnswers,
  SellerQuestionnaireOwnerDetails,
  SellerQuestionnairePropertyDetails,
  SellerQuestionnaireRealtorDetails
} from "../@types/sharedSchema";

/**
 * Public (no-auth) client for the NATIVE signing ceremony. These endpoints are
 * slug + OTP gated on the server; the signer isn't logged in, so we use a plain
 * axios (NOT the token-attaching instance in `signatures.ts`). Every call that
 * acts as the signer carries this browser's signer session (`signerSession.ts`).
 */

export type SigningFieldArea = {
  x: number;
  y: number;
  w: number;
  h: number;
  page: number;
};

export type SigningField = {
  name: string;
  type: string;
  role: string;
  required: boolean;
  title?: string;
  description?: string;
  areas: SigningFieldArea[];
  validationPattern?: string;
  readonly?: boolean;
  defaultValue?: string;
  fontSize?: number;
  align?: string;
  dateFormat?: string;
};

export type SigningContent = {
  documentId: string;
  slug: string;
  status: string;
  role: string;
  totalPages: number;
  sentPdfUrl: string;
  sentHtml: string;
  artifactRevision?: string;
  fields: SigningField[];
  prefilledValues: Record<string, string>;
  consentDisclosureVersion: string;
  /**
   * Hidden-addresses document: after the main signature the server returns an
   * addresses addendum (full addresses + signature field) as a SECOND signing
   * step. The addendum's content is only revealed by the submit response.
   */
  hasAddendum?: boolean;
  /** True when the document carries a seller questionnaire the signer must fill AFTER the main signature. */
  hasQuestionnaire?: boolean;
  /**
   * 'addendum' when the main document is already signed and only the
   * addresses addendum is left — the payload's html/fields ARE the addendum
   * (resume-after-refresh) and submission goes to /submit-addendum.
   * 'questionnaire' when only the seller questionnaire is left — the coded
   * form renders from `questionnaire` and submission goes to
   * /submit-questionnaire.
   */
  phase?: "main" | "addendum" | "questionnaire";
  /** The questionnaire payload — present only when `phase` is 'questionnaire' (resume-after-refresh). */
  questionnaire?: SigningQuestionnaire;
  /** 'ownerQuestionnaire': the questionnaire is the whole document (no main step). */
  documentKind?: SignatureDocumentKind;
};

/** The addresses-addendum payload returned by the main submit on hidden-addresses docs. */
export type SigningAddendum = {
  html: string;
  /** Prepared addendum PDF, paired with this addendum's field geometry. */
  sentPdfUrl?: string;
  artifactRevision?: string;
  totalPages: number;
  fields: SigningField[];
};

/**
 * The seller-questionnaire payload (prefill for the coded form) returned by
 * the submit that unlocks the questionnaire phase, or by /view on resume.
 * Mirrors REST_schema.PublicSellerQuestionnairePayload.
 */
export type SigningQuestionnaire = {
  version: string;
  property: SellerQuestionnairePropertyDetails;
  realtor: SellerQuestionnaireRealtorDetails;
  owner: SellerQuestionnaireOwnerDetails;
  documentFormattedNumber: string;
  documentTitle: string;
};

export type SubmitResult = {
  status: string;
  signedDocumentUrl: string | null;
  /** Present when the signer still has to sign the addresses addendum. */
  addendum?: SigningAddendum;
  /** Present when the signer still has to fill + sign the seller questionnaire. */
  questionnaire?: SigningQuestionnaire;
};

export type SigningClientContext = { sessionId?: string; timezone?: string };

const base = `${WEBOT_SERVER_ENDPOINT}/api/v1/signatures/public/documents`;

export const getSigningContent = async (slug: string, ctx?: SigningClientContext): Promise<SigningContent> => {
  const res = await axios.post<SigningContent>(
    `${base}/${encodeURIComponent(slug)}/view`,
    { sessionId: ctx?.sessionId, timezone: ctx?.timezone },
    { headers: signerSessionHeaders() }
  );
  return res.data;
};

export const submitSignature = async (
  slug: string,
  body: {
    fieldValues: Record<string, string>;
    consentAccepted: boolean;
    consentDisclosureVersion: string;
    sessionId?: string;
    timezone?: string;
    expectedArtifactRevision?: string;
  }
): Promise<SubmitResult> => {
  const res = await axios.post<SubmitResult>(`${base}/${encodeURIComponent(slug)}/submit`, body, {
    headers: signerSessionHeaders()
  });
  return res.data;
};

/**
 * Second signing phase for hidden-addresses documents — submits the addendum
 * signature after the main submit returned an `addendum` payload.
 */
export const submitAddendumSignature = async (
  slug: string,
  body: {
    fieldValues: Record<string, string>;
    sessionId?: string;
    timezone?: string;
    expectedArtifactRevision?: string;
  }
): Promise<SubmitResult> => {
  const res = await axios.post<SubmitResult>(
    `${base}/${encodeURIComponent(slug)}/submit-addendum`,
    body,
    { headers: signerSessionHeaders() }
  );
  return res.data;
};

/**
 * Final signing phase for seller-questionnaire documents — submits the
 * statutory questionnaire answers + owner signature after the previous phase
 * returned a `questionnaire` payload.
 */
export const submitQuestionnaireSignature = async (
  slug: string,
  body: {
    answers: SellerQuestionnaireAnswers;
    signatureDataUrl: string;
    /** A questionnaire-only document: the consent and the revision /view served ride this submit. */
    consentAccepted?: boolean;
    consentDisclosureVersion?: string;
    expectedArtifactRevision?: string;
    sessionId?: string;
    timezone?: string;
  }
): Promise<SubmitResult> => {
  const res = await axios.post<SubmitResult>(
    `${base}/${encodeURIComponent(slug)}/submit-questionnaire`,
    body,
    { headers: signerSessionHeaders() }
  );
  return res.data;
};

export const declineSignature = async (
  slug: string,
  body?: { reason?: string; sessionId?: string; timezone?: string }
): Promise<{ status: string }> => {
  const res = await axios.post<{ status: string }>(`${base}/${encodeURIComponent(slug)}/decline`, body || {}, {
    headers: signerSessionHeaders()
  });
  return res.data;
};

// ===== Completed-document viewer (`/d/:documentId`) =====

export type DocumentViewMeta = {
  documentId: string;
  title: string;
  formattedNumber: string;
  status: string;
  completedAt?: string;
  /** Whether a standalone seller-questionnaire PDF is available for download. */
  hasQuestionnaire?: boolean;
  /** Names the questionnaire: a rental's is שאלון בעל זכות בנכס. */
  questionnaireListedFor?: ListingListedFor;
  /** 'ownerQuestionnaire': the questionnaire IS the document — one file, no tabs. */
  documentKind?: SignatureDocumentKind;
};

/** Lean metadata for the public viewer page. 404s for non-completed docs. */
export const getDocumentViewMeta = async (documentId: string): Promise<DocumentViewMeta> => {
  const res = await axios.get<DocumentViewMeta>(`${base}/${encodeURIComponent(documentId)}/meta`);
  return res.data;
};

/**
 * Absolute URL that streams a completed document's signed PDF (default) or the
 * standalone seller questionnaire (`type: "questionnaire"`). The server proxies
 * the bytes from storage, so this URL never exposes the underlying GCS link.
 * `download: true` forces a save dialog; otherwise the PDF renders inline (used
 * in the iframe).
 *
 * The audit certificate is deliberately NOT reachable here — it is realtor-only
 * and served from the authenticated `downloadSignedPdfBlob(id, "audit")` path,
 * never from this public signer-facing endpoint.
 */
export const getDocumentFileUrl = (documentId: string, opts?: { type?: "signed" | "questionnaire"; download?: boolean }): string => {
  const params = new URLSearchParams();
  if (opts?.type === "questionnaire") params.set("type", "questionnaire");
  if (opts?.download) params.set("download", "1");
  const qs = params.toString();
  return `${base}/${encodeURIComponent(documentId)}/file${qs ? `?${qs}` : ""}`;
};

/** In-app path to the branded viewer page for a completed document. */
export const getDocumentViewPath = (documentId: string): string => `/d/${encodeURIComponent(documentId)}`;
